'use client';
import { CallStatus } from '../voice/CallStatus';
import { useVoiceCallController } from '../voice/callController';
import { useDictation } from '../voice/useDictation';
import { flushSync } from 'react-dom';
import { memo, useEffect, useId, useRef, useState, type RefObject } from 'react';
import { PhoneOff, Send, Square, Paperclip, ChevronRight } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
import { useMediaQuery } from '../../lib/useMobile';
import { useToast } from '../../components/ui/Toast';
import { Button } from '../../components/ui/Button';
import { LabelMark } from '../../components/ui/StatusMark';
import { ChatTextarea, useChatTextareaSize } from './ChatTextarea';
import { ComposerActions } from './ComposerActions';
import { useBrainChat, useBrainChatInput, useBrainChatStatus } from './BrainChatProvider';
import { useBrainCommands } from '../../lib/queries';
import { parseSlashInvocation } from '../../lib/slashCommands';
import { childReadOnlyText } from '../../lib/chatPresentation';
import { PromptHistoryCursor } from './promptHistory';
import { usePromptHistory } from './usePromptHistory';
import { activeMentionQuery, mentionLabel, singleEdit, type MentionPerson } from 'elowen-plugin-shared/composerMentions';
import { MentionPicker, type MentionPick, type MentionPickerHandle, type MentionTarget } from '../../components/ui/MentionPicker';
import { AGENT_AVATAR_SRC } from './agentIdentity';
import type { Profile } from '../../lib/types';

/** What a conversation offers the composer's `@`. Absent, or with nobody to list, `@` is plain text. */
export interface ComposerMentions {
  /** The agent as a mention target, only in a shared conversation; `paused` says the mention wakes it. */
  agent: { paused: boolean } | null;
  /** Members the viewer can mention: the owner and participants, the viewer excluded. */
  participants: readonly Profile[];
  /** The people the owner may invite (members of the conversation's Project); empty for a guest and until read. */
  candidates: readonly Profile[];
  /** Asks for {@link candidates} once, at the conversation's first typed `@`, so opening a conversation
   *  reads nothing. */
  loadCandidates: () => void;
  /** Invites a candidate; rejects with the localized refusal, which the confirmation shows. */
  invite: (person: MentionPerson) => Promise<void>;
}

/** Only this editor subscribes to draft changes. The shared controller still owns the draft and send
 *  actions, but a keystroke does not render the transcript, top bar, cards or telemetry — and a streamed
 *  token does not render the editor, because nothing it reads changes per token. */
/** How long send must be held before it starts dictating; a shorter press is a normal click. */
const HOLD_TO_DICTATE_MS = 400;

/** One whitespace character typed by the space key: a plain space or a no-break space. */
function isLoneSpace(text: string | null): boolean {
  return text !== null && text.length === 1 && text.trim() === '';
}

export const ChatComposer = memo(function ChatComposer({ variant, composerRef, mentions }: {
  variant: 'full' | 'compact';
  composerRef: RefObject<HTMLTextAreaElement | null>;
  /** The conversation's `@` people; absent (no bound writable conversation) `@` stays plain text. */
  mentions?: ComposerMentions;
}) {
  const { t, locale } = useTranslation();
  const wideViewport = useMediaQuery('(min-width: 640px)');
  // The desktop dock is narrow even on a wide screen; its hint must fit one row too.
  const placeholder = variant === 'compact' || wideViewport === false
    ? t.brainChat.placeholderShort
    : wideViewport === true ? t.brainChat.placeholder : '';
  const { setInput, acceptPromptSuggestion, addFiles, submit, commands, runSlash, onQueueRecall, abort, exitReadOnly, activeSessionId, draft, reportTyping } = useBrainChat();
  const { promptSuggestion, attachments, uploads, queued, busy, canStop, readOnly, readOnlyChild, childDelegation, childFocus, childFocusName } = useBrainChatStatus();
  const voice = useVoiceCallController();
  const callActionRef = useRef<HTMLButtonElement>(null);
  // A call owns the composer's primary action even if another conversation is being previewed.
  const readOnlyLocked = readOnly !== null;
  const voiceLocked = !!voice?.active;
  const locked = readOnlyLocked || voiceLocked;
  const childLock = childReadOnlyText(locale, childDelegation ?? { status: 'running' });
  const lockDescription = readOnlyChild ? childLock.description : t.brainChat.readOnly;
  const wasCalling = useRef(voiceLocked);
  useEffect(() => {
    if (wasCalling.current && !voiceLocked && !readOnlyLocked) composerRef.current?.focus();
    wasCalling.current = voiceLocked;
  }, [voiceLocked, readOnlyLocked, composerRef]);
  const input = useBrainChatInput();
  const promptHistory = usePromptHistory();
  const historyCursor = useRef(new PromptHistoryCursor());
  useEffect(() => { historyCursor.current.reset(); }, [activeSessionId, promptHistory.userId]);
  const [slashIdx, setSlashIdx] = useState(0);
  const { toast } = useToast();
  // Dictation writes into this textarea while the person speaks. Send stays available meanwhile: it ends the
  // dictation first and sends the text once the last words are final (a short, bounded wait).
  const dictation = useDictation({
    read: () => { const el = composerRef.current; return el ? { value: el.value, start: el.selectionStart, end: el.selectionEnd } : null; },
    write: (value, caret) => {
      const el = composerRef.current;
      if (!el) return;
      flushSync(() => setInput(value));
      el.setSelectionRange(caret, caret);
    },
  }, (failure) => toast(t.voice.dictation.errors[failure], 'error'));
  const { beforeEdit } = dictation;
  const acceptOnSpace = useRef<(() => void) | null>(null);
  const compositionRange = useRef<{ start: number; end: number } | null>(null);
  const pendingEdit = useRef<{ previous: string; start: number; end: number; inserted: string; composing: boolean } | null>(null);
  /** The field's text as it stood when the pending browser edit began, attributable or not. */
  const previousValue = useRef('');
  useEffect(() => { compositionRange.current = null; pendingEdit.current = null; }, [activeSessionId]);
  useEffect(() => {
    const element = composerRef.current;
    if (!element) return;
    const beforeInput = (event: InputEvent): void => {
      pendingEdit.current = null;
      previousValue.current = element.value;
      // A phone keyboard has no Tab: a lone space typed into the empty field takes the suggestion. Cancelling
      // the insertion keeps the space out of the field, so the keyboard (iOS) has no typed text to put back.
      const accept = acceptOnSpace.current;
      if (accept && event.cancelable && event.inputType === 'insertText' && element.value === '' && isLoneSpace(event.data)) {
        event.preventDefault();
        accept();
        return;
      }
      const selection = { start: element.selectionStart, end: element.selectionEnd };
      beforeEdit(selection.start, selection.end);
      const ranges = event.getTargetRanges?.() ?? [];
      const target = ranges.length === 1 && ranges[0]!.startContainer === element && ranges[0]!.endContainer === element
        ? { start: ranges[0]!.startOffset, end: ranges[0]!.endOffset } : null;
      const range = target ?? (event.inputType === 'insertCompositionText' ? compositionRange.current : selection);
      if (!range || range.start < 0 || range.end < range.start || range.end > element.value.length || draft.getSnapshot() !== element.value) {
        compositionRange.current = null;
        return;
      }
      // A collapsed deletion without a browser target does not identify the deleted grapheme or word.
      // Leave it to the native editor and let the draft invalidate identities rather than infer a range.
      const inserted = event.inputType === 'insertText' || event.inputType === 'insertCompositionText'
        || (event.inputType === 'insertReplacementText' && target !== null)
        ? event.data : event.inputType === 'insertLineBreak' || event.inputType === 'insertParagraph' ? '\n'
          : event.inputType === 'insertFromPaste' ? event.dataTransfer?.getData('text/plain') ?? event.data
            : event.inputType.startsWith('delete') && (target !== null || range.start !== range.end) ? '' : null;
      if (inserted !== null) {
        // Updating a controlled textarea here would precede the browser's own insertion and duplicate it.
        pendingEdit.current = { previous: element.value, ...range, inserted, composing: event.inputType === 'insertCompositionText' };
      } else if (event.isComposing) compositionRange.current = null;
    };
    const compositionStart = (): void => { compositionRange.current = { start: element.selectionStart, end: element.selectionEnd }; };
    const compositionEnd = (): void => { compositionRange.current = null; };
    element.addEventListener('beforeinput', beforeInput);
    element.addEventListener('compositionstart', compositionStart);
    element.addEventListener('compositionend', compositionEnd);
    return () => { element.removeEventListener('beforeinput', beforeInput); element.removeEventListener('compositionstart', compositionStart); element.removeEventListener('compositionend', compositionEnd); };
  }, [composerRef, beforeEdit, draft]);
  const dictating = dictation.state !== 'idle';
  const suggestionHintId = useId();
  const suggestion = !locked && !busy && !dictating && input === '' && attachments.length === 0 && uploads.length === 0 ? promptSuggestion : null;
  useEffect(() => { acceptOnSpace.current = suggestion ? acceptPromptSuggestion : null; }, [suggestion, acceptPromptSuggestion]);
  const sendable = input.trim() !== '' || attachments.length > 0 || dictating;
  const currentSession = useRef({ id: activeSessionId, revision: 0 });
  if (currentSession.current.id !== activeSessionId) currentSession.current = { id: activeSessionId, revision: currentSession.current.revision + 1 };
  const sendDictation = (): void => {
    const revision = currentSession.current.revision;
    reportTyping(false);
    void dictation.finish().then(() => {
      if (currentSession.current.revision === revision) return submit('steer');
    });
  };
  // Holding send dictates for as long as it is held; releasing it is the tick. The press that started a
  // dictation must not also send, so its click is swallowed once.
  const holdCapable = !!voice?.available && !voice.active;
  const holdToDictate = holdCapable && !dictating;
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holdStarted = useRef(false);
  const swallowClick = useRef(false);
  const endHold = (): void => {
    if (holdTimer.current) { clearTimeout(holdTimer.current); holdTimer.current = null; }
    if (!holdStarted.current) return;
    holdStarted.current = false;
    swallowClick.current = true;
    dictation.stop();
  };
  useEffect(() => () => { if (holdTimer.current) clearTimeout(holdTimer.current); }, []);
  const fileRef = useRef<HTMLInputElement>(null);
  const slashQuery = !locked && input.startsWith('/') && !/\s/.test(input) ? input.slice(1).toLowerCase() : null;
  const slashItems = (slashQuery !== null ? commands.filter((command) => command.name.startsWith(slashQuery)) : [])
    .map((command) => ({ key: command.name, label: `/${command.name}`, desc: command.description, command, run: () => runSlash(command, undefined, { fromComposer: true }) }));
  const slashOpen = slashItems.length > 0;
  const { refetch: refreshCommands } = useBrainCommands();
  const commandDraft = slashQuery !== null;
  useEffect(() => { if (commandDraft) void refreshCommands(); }, [commandDraft, refreshCommands]);
  const slashSel = Math.min(slashIdx, slashItems.length - 1);

  // `@` at a word start opens the people picker wherever the caret is, in any conversation that offers
  // someone to mention or invite; the picker itself stays closed while nobody matches. The caret is kept
  // with the text it was read from, so a draft replaced from elsewhere (history recall, a stash, a restored
  // send) opens nothing until the person types or moves the caret in it, and a selected range is no caret.
  // Escape closes that one `@`; a new `@` offers the picker again.
  const mentionPicker = useRef<MentionPickerHandle>(null);
  const mentionable = !!mentions && !locked;
  const [caret, setCaret] = useState<{ text: string; at: number } | null>(null);
  const [mentionDismissed, setMentionDismissed] = useState<number | null>(null);
  const [mentionList, setMentionList] = useState<string>();
  const [mentionOption, setMentionOption] = useState<string>();
  const typedMention = mentionable && caret?.text === input ? activeMentionQuery(input, caret.at) : null;
  const mentionTarget: MentionTarget | null = caret && typedMention && typedMention.start !== mentionDismissed
    ? { start: typedMention.start, end: caret.at, query: typedMention.query }
    : null;
  const trackCaret = (text: string, start: number, end: number): void => {
    if (!mentionable) return;
    if (start !== end) { setCaret(null); return; }
    setCaret((previous) => previous?.text === text && previous.at === end ? previous : { text, at: end });
    if (activeMentionQuery(text, end)) mentions.loadCandidates();
    else setMentionDismissed(null);
  };
  const insertMention = (target: MentionTarget, pick: MentionPick): void => {
    let at = target.end;
    const account = pick === 'agent' ? { id: pick, label: t.chat.roleElowen } : { id: pick.id, label: mentionLabel(pick) };
    flushSync(() => { at = draft.insertMention(target.start, target.end, account); });
    // The same bookkeeping as a typed edit: history browsing ends and a running dictation sees the new text.
    historyCursor.current.reset();
    dictation.edit(draft.getSnapshot(), at);
    reportTyping(draft.getSnapshot().trim() !== '');
    composerRef.current?.setSelectionRange(at, at);
    trackCaret(draft.getSnapshot(), at, at);
  };

  useChatTextareaSize(composerRef, input, locked);

  return (
      <form
        onDragOver={(e) => { if (e.dataTransfer.types.includes('Files')) e.preventDefault(); }}
        onDrop={(e) => {
          const files = Array.from(e.dataTransfer.files);
          if (!files.length) return;
          e.preventDefault();
          if (!locked) void addFiles(files);
        }}
        className={variant === 'full'
          ? 'chat-composer relative flex flex-wrap items-end gap-1 rounded-2xl border border-border bg-card p-1.5 transition-colors focus-within:border-border-strong'
          : 'relative flex flex-wrap items-end gap-2 p-2'}
        onSubmit={(e) => {
          e.preventDefault();
          if (locked || uploads.length > 0) return;
          if (dictating) { sendDictation(); return; }
          if (!sendable) return;
          const invocation = draft.commandForDraft(input) ? null : parseSlashInvocation(input, commands);
          // Prompt macros and unknown slash-prefixed prose are real chat turns. A published daemon command
          // with arguments is executed through the same catalog path as a menu pick; local pickers keep their
          // previous argument-bearing text behaviour unless the menu invoked them bare.
          if (invocation && invocation.command.kind !== 'prompt'
            && (!invocation.argument || invocation.command.execution === 'session-control')) {
            runSlash(invocation.command, invocation.argument, { fromComposer: true });
            return;
          }
          reportTyping(false);
          // The send control swaps to Stop while the turn runs, so a pointer send unmounts the element that
          // held focus and leaves it on the document body — outside the chat surface, where the chat keymap
          // (Esc to stop, ↑ to recall) does not reach. The emptied field is where typing continues, so it
          // takes the focus back on the keyboard and pointer paths alike.
          composerRef.current?.focus({ preventScroll: true });
          void submit('steer');
        }}
      >
        {slashOpen && (
          <div data-testid="chat-slash-menu" className={`absolute bottom-full w-full max-w-md overflow-hidden rounded-lg border border-border bg-muted shadow-raised ${variant === 'full' ? 'left-0 mb-2' : 'left-2 mb-1'}`}>
            <div className="max-h-60 overflow-y-auto py-1">
              {slashItems.map((it, i) => (
                <Button
                  key={it.key}
                  type="button"
                  variant="ghost"
                  onMouseDown={(e) => { e.preventDefault(); it.run(); }}
                  onMouseEnter={() => setSlashIdx(i)}
                  className={`h-auto w-full items-center justify-start gap-2 rounded-none px-3 py-1.5 text-left ${i === slashSel ? 'bg-primary/15 text-foreground' : ''}`}
                >
                  <span className="shrink-0 font-mono">{it.label}</span>
                  {it.command.plugin === 'skills' && it.command.kind === 'prompt' && <LabelMark tone="muted">{t.brainChat.skillLabel}</LabelMark>}
                  {it.desc && <span className="truncate text-small opacity-60">{it.desc}</span>}
                </Button>
              ))}
            </div>
          </div>
        )}
        {mentions && (
          <MentionPicker
            ref={mentionPicker}
            fieldRef={composerRef}
            target={mentionTarget}
            participants={mentions.participants}
            candidates={mentions.candidates}
            agent={mentions.agent ? { label: t.chat.roleElowen, src: AGENT_AVATAR_SRC, hint: mentions.agent.paused ? t.chat.mentionAgentHint : undefined } : null}
            placeholder={t.chat.mentionPlaceholder}
            invite={{ title: t.chat.inviteConfirmTitle, body: t.chat.inviteConfirmBody, confirm: t.chat.inviteConfirm, failed: t.chat.inviteFailed.generic }}
            onInsert={insertMention}
            onInvite={mentions.invite}
            onDismiss={() => setMentionDismissed(typedMention?.start ?? null)}
            onAria={(list, option) => { setMentionList(list); setMentionOption(option); }}
          />
        )}
        {!locked && <input
          ref={fileRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => { if (e.target.files) void addFiles(e.target.files); e.target.value = ''; }}
        />}
        {!voiceLocked && <Button
          variant="ghost" size="icon" type="button"
          data-testid={readOnlyLocked ? 'chat-read-only-exit' : 'chat-attach'}
          onClick={readOnlyLocked ? exitReadOnly : () => fileRef.current?.click()}
          aria-label={readOnlyLocked ? t.brainChat.readOnlyExit : t.brainChat.attach}
          title={readOnlyLocked ? (childFocusName ? `${childFocusName} · ${t.brainChat.readOnlyExit}` : t.brainChat.readOnlyExit) : t.brainChat.attach}
          // While a dictation runs, its status and cancel take the actions slot; a phone hides the paperclip
          // meanwhile so the draft keeps a readable width (PHONE_MAX_WIDTH = 767 → `max-md:`).
          className={`${variant === 'full' ? 'rounded-xl' : 'rounded-lg border-border'} ${dictating ? 'max-md:hidden' : ''}`}
        >
          {readOnlyLocked ? <ChevronRight size={16} className="rotate-180" aria-hidden /> : <Paperclip size={16} aria-hidden />}
        </Button>}
        {voiceLocked && voice && <CallStatus snapshot={voice.snapshot} onDismiss={voice.dismiss} />}
        {suggestion && <span id={suggestionHintId} className="sr-only">{t.brainChat.suggestionHint}</span>}
        <ChatTextarea
          variant={variant}
          hidden={voiceLocked}
          ref={composerRef}
          data-testid="chat-composer"
          value={locked ? '' : input}
          disabled={locked}
          readOnly={locked}
          aria-label={locked ? lockDescription : undefined}
          aria-describedby={suggestion ? suggestionHintId : undefined}
          aria-keyshortcuts={suggestion ? 'Tab Space' : undefined}
          aria-autocomplete={mentionable ? 'list' : undefined}
          aria-controls={mentionList}
          aria-activedescendant={mentionOption}
          title={locked ? lockDescription : suggestion ?? undefined}
          onChange={(e) => {
            const edit = pendingEdit.current;
            pendingEdit.current = null;
            if (locked) return;
            // An insertion the browser cannot cancel (keyboard composition) reaches the field; the lone space
            // still takes the suggestion here, read from the changed value rather than a key the keyboard may not report.
            if (suggestion && isLoneSpace(e.target.value)) { acceptPromptSuggestion(); return; }
            const value = e.target.value;
            const previous = edit?.previous ?? previousValue.current;
            const exact = edit && draft.getSnapshot() === edit.previous
              && value === edit.previous.slice(0, edit.start) + edit.inserted + edit.previous.slice(edit.end);
            if (exact) {
              draft.applyMentionEdit(edit.start, edit.end, edit.inserted);
              if (edit.composing) compositionRange.current = { start: edit.start, end: edit.start + edit.inserted.length };
            } else {
              compositionRange.current = null;
              // A textarea reports no target ranges for a deletion, so the change itself names the replaced
              // range: one contiguous replacement keeps every mention it does not touch, anything less clear
              // still invalidates them.
              const derived = draft.getSnapshot() === previous ? singleEdit(previous, value) : null;
              if (derived) draft.applyMentionEdit(derived.start, derived.end, derived.inserted);
              else setInput(value);
            }
            historyCursor.current.reset(); dictation.edit(value, e.target.selectionEnd); reportTyping(value.trim() !== ''); setSlashIdx(0);
            trackCaret(e.target.value, e.target.selectionStart, e.target.selectionEnd);
          }}
          onBlur={() => reportTyping(false)}
          onSelect={(e) => trackCaret(e.currentTarget.value, e.currentTarget.selectionStart, e.currentTarget.selectionEnd)}
          onKeyDown={(e) => {
            if (locked) { e.preventDefault(); return; }
            if (e.nativeEvent.isComposing) return;
            if (mentionPicker.current?.keyDown(e)) return;
            const plainArrow = !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey;
            if (slashOpen) {
              if (e.key === 'ArrowDown') { e.preventDefault(); setSlashIdx((i) => (Math.min(i, slashItems.length - 1) + 1) % slashItems.length); return; }
              if (e.key === 'ArrowUp') { e.preventDefault(); setSlashIdx((i) => (Math.min(i, slashItems.length - 1) - 1 + slashItems.length) % slashItems.length); return; }
              if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); slashItems[slashSel]?.run(); return; }
              if (e.key === 'Escape') { e.preventDefault(); setInput(''); return; }
            }
            if (suggestion && e.key === 'Tab' && !e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey) {
              e.preventDefault(); acceptPromptSuggestion(); return;
            }
            if (plainArrow && e.key === 'Escape') {
              if (busy) { e.preventDefault(); if (canStop) abort(); return; }
              if (queued.some(entry => entry.removable)) { e.preventDefault(); onQueueRecall(true); return; }
            }
            // ↑ with empty input → recall the last queued message the viewer may take back; in a shared
            // conversation someone else's entry stays queued and ↑ goes on to prompt history.
            const recallable = plainArrow && e.key === 'ArrowUp' && input === '' ? queued.findLast((entry) => entry.removable) : undefined;
            if (recallable) {
              e.preventDefault();
              onQueueRecall();
              return;
            }
            if (plainArrow && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
              const el = e.currentTarget;
              const recalled = historyCursor.current.navigate(e.key, input, el.selectionStart, el.selectionEnd, promptHistory.read());
              if (recalled) {
                e.preventDefault();
                flushSync(() => setInput(recalled.text));
                el.setSelectionRange(recalled.caret, recalled.caret);
                return;
              }
            }
            // Keyboard and pointer submission share the form's command routing and dictation handling.
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); e.currentTarget.form!.requestSubmit(); }
          }}
          onPaste={(e) => {
            if (locked) { e.preventDefault(); return; }
            const files = Array.from(e.clipboardData.files);
            if (files.length) { e.preventDefault(); void addFiles(files); }
          }}
          rows={1}
          wrap={locked || suggestion ? 'off' : undefined}
          placeholder={readOnlyLocked ? (readOnlyChild ? childLock.label : t.brainChat.readOnly) : suggestion ?? placeholder}
          className={locked || suggestion ? 'whitespace-nowrap placeholder:whitespace-nowrap overflow-hidden text-ellipsis placeholder:overflow-hidden placeholder:text-ellipsis' : ''}
        />
        {/* Editable views place the next message's actions (work mode, voice) beside send/stop.
            Locked views have no next message, so they do not offer this menu. */}
        {!locked && <ComposerActions variant={variant} composerRef={composerRef} callActionRef={callActionRef} dictation={dictation} />}
        {busy && (!locked || childFocus || voiceLocked) && (
          <Button variant="accent" size="icon"
            type="button"
            data-testid="chat-stop"
            onClick={abort}
            disabled={!canStop}
            title={canStop ? undefined : t.chat.ownerOnlyControl}
            aria-label={t.brainChat.stop}
            /* `animate-stop-pulse` must stay a literal class in TSX: it is a plain CSS class defined once
               in app/styles/animations.css, so Tailwind's content purge keeps it only because it sees the
               string here. While a turn runs the button breathes a slow primary halo (see stop-pulse);
               hover/focus stay readable on top of it, and quiet-effects/reduced-motion silence it. */
            // The full composer's loud button is the `accent` variant, chosen above, so it states only its
            // radius here; the compact one is the toned-down twin of it, which no variant covers and which
            // therefore keeps its own skin.
            className={`animate-stop-pulse ${variant === 'full'
              ? 'rounded-xl'
              : 'rounded-lg border border-primary bg-primary/15 text-primary hover:bg-primary/25'}`}
          >
            <Square size={14} fill="currentColor" aria-hidden />
          </Button>
        )}
        {voiceLocked && voice ? <Button ref={callActionRef} variant="danger" size="icon" type="button" icon={PhoneOff}
          data-testid="voice-call-hangup" aria-label={t.voice.call.hangup} title={t.voice.call.hangup}
          autoFocus onClick={voice.hangup} disabled={voice.snapshot.state === 'ending'}
          className={variant === 'full' ? 'rounded-xl' : 'rounded-lg'} /> : !busy && (
          <Button variant="accent" size="icon"
            type="submit"
            data-testid="chat-send"
            // An empty draft keeps the button pressable when holding it can dictate: a disabled button gets no
            // pointer events. The submit handler below ignores the empty click.
            disabled={locked || uploads.length > 0 || (!sendable && !holdToDictate)}
            aria-disabled={!locked && uploads.length === 0 && !sendable && holdToDictate ? true : undefined}
            aria-label={t.brainChat.send}
            onPointerDown={(e) => {
              swallowClick.current = false;
              if (!holdToDictate || locked || e.button !== 0) return;
              holdTimer.current = setTimeout(() => { holdTimer.current = null; holdStarted.current = true; void dictation.start(); }, HOLD_TO_DICTATE_MS);
            }}
            onPointerUp={endHold}
            onPointerCancel={endHold}
            onPointerLeave={endHold}
            onContextMenu={holdCapable ? (e) => e.preventDefault() : undefined}
            onClick={(e) => { if (swallowClick.current) { e.preventDefault(); swallowClick.current = false; } }}
            className={`${holdCapable ? 'touch-none select-none [-webkit-touch-callout:none] ' : ''}${!sendable && holdToDictate ? 'opacity-40 ' : ''}${
              variant === 'full'
                ? 'rounded-xl'
                : 'rounded-lg border border-primary bg-primary/15 text-primary hover:bg-primary/25 disabled:hover:bg-primary/15 disabled:hover:text-primary'
            }`}
          >
            <Send size={16} aria-hidden />
          </Button>
        )}
        {!voiceLocked && voice && <CallStatus snapshot={voice.snapshot} onDismiss={voice.dismiss} />}
      </form>
  );
});
